// Game state and logic for Space Invaders
export class GameState {
  constructor(canvas) {
    this.canvas = canvas;
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.running = true;
    this.gameOver = false;
    this.lastTime = null;
    this.highscore = this.loadHighscore();

    // Player
    this.player = {
      w: 46,
      h: 18,
      x: 0, // Will be set in setupEnemies
      y: 0, // Will be set in setupEnemies
      speed: 260,
      cooldown: 0
    };

    // Game objects
    this.bullets = [];
    this.enemyBullets = [];
    this.enemies = [];
    this.barriers = [];

    // Enemy configuration
    this.enemyRows = 5;
    this.enemyCols = 10;
    this.enemyGap = 14;
    this.enemyW = 32;
    this.enemyH = 18;
    this.enemyX = 60;
    this.enemyY = 70;
    this.enemyDir = 1;
    this.enemySpeed = 15; // Redusert fra 20
    this.enemyDrop = 18;

    // Initialize after a short delay to ensure canvas is ready
    setTimeout(() => {
      this.setupEnemies();
      this.setupBarriers();
    }, 50);
  }

  setupEnemies() {
    this.enemies = [];
    const cols = this.enemyCols;
    const rows = this.enemyRows;
    
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = this.enemyX + c * (this.enemyW + this.enemyGap);
        const y = this.enemyY + r * (this.enemyH + this.enemyGap);
        const tint = r % 3;
        this.enemies.push({
          x, y, row: r, col: c, w: this.enemyW, h: this.enemyH, alive: true, tint
        });
      }
    }

    // Center if canvas wide
    const totalW = cols * (this.enemyW + this.enemyGap) - this.enemyGap;
    this.enemyX = Math.max(28, (this.canvas.width / (window.devicePixelRatio || 1) - totalW) / 2);
    
    // Set player position
    this.player.x = this.canvas.width / 2 / (window.devicePixelRatio || 1);
    this.player.y = this.canvas.height / (window.devicePixelRatio || 1) - 70;
  }

  setupBarriers() {
    this.barriers = [];
    const count = 4;
    const spacing = (this.canvas.width / (window.devicePixelRatio || 1)) / (count + 1);
    
    for (let i = 1; i <= count; i++) {
      const bx = spacing * i - 36;
      const by = this.canvas.height / (window.devicePixelRatio || 1) - 140;
      this.barriers.push({
        x: bx, y: by, w: 72, h: 44, mask: this.createBarrierMask(72, 44)
      });
    }
  }

  createBarrierMask(w, h) {
    // Small pixelated mask for progressive damage
    const cols = Math.floor(w / 6);
    const rows = Math.floor(h / 6);
    const mask = [];
    
    for (let r = 0; r < rows; r++) {
      mask[r] = [];
      for (let c = 0; c < cols; c++) {
        mask[r][c] = true;
      }
    }
    
    return { grid: mask, cellW: w / cols, cellH: h / rows, cols, rows };
  }

  reset() {
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.running = true;
    this.gameOver = false;
    this.lastTime = null;
    this.bullets = [];
    this.enemyBullets = [];
    this.enemySpeed = 20;
    this.enemyCols = 10;
    this.enemyW = 32;
    
    this.player.cooldown = 0;
    
    // Ensure canvas is ready before setup
    if (this.canvas.width > 0) {
      this.setupEnemies();
      this.setupBarriers();
    } else {
      setTimeout(() => {
        this.setupEnemies();
        this.setupBarriers();
      }, 50);
    }
  }

  nextLevel() {
    this.level++;
    this.enemySpeed += 8; // Redusert fra 12
    this.enemyCols = Math.max(6, this.enemyCols - (this.level > 6 ? 1 : 0));
    this.enemyW = Math.max(24, this.enemyW - (this.level > 4 ? 2 : 0));
    
    // Ensure canvas is ready before setup
    if (this.canvas.width > 0) {
      this.setupEnemies();
      this.setupBarriers();
    } else {
      setTimeout(() => {
        this.setupEnemies();
        this.setupBarriers();
      }, 50);
    }
  }

  getAliveEnemies() {
    if (!this.enemies) return [];
    return this.enemies.filter(e => e.alive);
  }

  isLevelComplete() {
    if (!this.enemies) return false;
    return this.getAliveEnemies().length === 0;
  }

  playerShoot() {
    if (this.player.cooldown <= 0) {
      this.bullets.push({
        x: this.player.x + this.player.w / 2,
        y: this.player.y - 6,
        vy: -420,
        r: 4
      });
      this.player.cooldown = 0.42;
      return true;
    }
    return false;
  }

  spawnEnemyBullets(dt) {
    if (!this.enemies) return false;
    
    // Probabilistic based on alive count and level
    if (Math.random() < 0.015 + this.level * 0.003) { // Redusert fra 0.02 + level * 0.004
      const byCol = {};
      this.enemies.forEach(e => {
        if (!e.alive) return;
        const col = e.col;
        // Pick lowest enemy in each column
        if (!byCol[col] || e.row > byCol[col].row) byCol[col] = e;
      });
      
      const available = Object.values(byCol);
      if (available.length) {
        const chosen = available[Math.floor(Math.random() * available.length)];
        this.enemyBullets.push({
          x: chosen.x + chosen.w / 2,
          y: chosen.y + chosen.h + 6,
          vy: 140 + this.level * 15, // Redusert fra 160 + level * 20
          r: 4
        });
        this.enemyShoot = true; // Flag for sound
        return true;
      }
    }
    return false;
  }

  checkCollisions() {
    if (!this.enemies || !this.barriers) return;
    
    const alive = this.getAliveEnemies();
    
    // Bullets -> enemies
    this.bullets.forEach(b => {
      alive.forEach(e => {
        if (!e.alive) return;
        const eb = { x: e.x, y: e.y, w: e.w, h: e.h };
        const bb = { x: b.x - b.r, y: b.y - b.r, w: b.r * 2, h: b.r * 2 };
        if (this.rectColl(eb, bb)) {
          e.alive = false;
          b._dead = true;
          this.score += 10 + (this.level - 1) * 2;
        }
      });
    });

    // Enemy bullets -> player
    this.enemyBullets.forEach(b => {
      const pb = { x: this.player.x, y: this.player.y, w: this.player.w, h: this.player.h };
      const bb = { x: b.x - b.r, y: b.y - b.r, w: b.r * 2, h: b.r * 2 };
      if (this.rectColl(pb, bb)) {
        b._dead = true;
        this.lives -= 1;
        this.player.x = (this.canvas.width / (window.devicePixelRatio || 1) - this.player.w) / 2;
        if (this.lives <= 0) {
          this.gameOver = true;
          this.running = false;
        }
      }
    });

    // Bullets -> barriers
    this.bullets.forEach(b => {
      this.barriers.forEach(bar => {
        if (b.y < bar.y + bar.h && b.y > bar.y - 6 && b.x > bar.x - 6 && b.x < bar.x + bar.w + 6) {
          const m = bar.mask;
          const cx = Math.floor((b.x - bar.x) / m.cellW);
          const cy = Math.floor((b.y - bar.y) / m.cellH);
          if (m.grid[cy] && m.grid[cy][cx]) {
            m.grid[cy][cx] = false;
            b._dead = true;
            this.barrierHit = true; // Flag for sound
          }
        }
      });
    });

    this.enemyBullets.forEach(b => {
      this.barriers.forEach(bar => {
        if (b.y < bar.y + bar.h && b.y > bar.y - 6 && b.x > bar.x - 6 && b.x < bar.x + bar.w + 6) {
          const m = bar.mask;
          const cx = Math.floor((b.x - bar.x) / m.cellW);
          const cy = Math.floor((b.y - bar.y) / m.cellH);
          if (m.grid[cy] && m.grid[cy][cx]) {
            m.grid[cy][cx] = false;
            b._dead = true;
            this.enemyBarrierHit = true; // Flag for sound
          }
        }
      });
    });

    // Clean up dead bullets
    this.bullets = this.bullets.filter(b => !b._dead);
    this.enemyBullets = this.enemyBullets.filter(b => !b._dead);
  }

  rectColl(a, b) {
    return !(a.x + (a.w || 0) < b.x || a.x > b.x + (b.w || 0) || a.y + (a.h || 0) < b.y || a.y > b.y + (b.h || 0));
  }

  update(dt) {
    if (!this.running || this.gameOver || !this.enemies) return;

    // Player movement
    let move = 0;
    if (this.controls.isMovingLeft()) move -= 1;
    if (this.controls.isMovingRight()) move += 1;
    this.player.x += move * this.player.speed * dt;
    this.player.x = Math.max(10, Math.min((this.canvas.width / (window.devicePixelRatio || 1)) - this.player.w - 10, this.player.x));

    if (this.controls.isShooting() && !this.controls.isShootHandled()) {
      this.playerShoot();
      this.controls.setShootHandled();
    }
    if (!this.controls.isShooting()) {
      this.controls.resetShootHandled();
    }

    if (this.player.cooldown > 0) this.player.cooldown -= dt;

    // Update bullets
    this.bullets.forEach(b => { b.y += b.vy * dt; });
    this.bullets = this.bullets.filter(b => b.y > -20);

    this.enemyBullets.forEach(b => { b.y += b.vy * dt; });
    this.enemyBullets = this.enemyBullets.filter(b => b.y < (this.canvas.height / (window.devicePixelRatio || 1)) + 50);

    // Check level completion
    if (this.isLevelComplete()) {
      this.nextLevel();
      return;
    }

    // Update enemies
    const alive = this.getAliveEnemies();
    let minX = Infinity, maxX = -Infinity;
    alive.forEach(e => {
      minX = Math.min(minX, e.x);
      maxX = Math.max(maxX, e.x + e.w);
    });

    const rightEdge = (this.canvas.width / (window.devicePixelRatio || 1)) - 8;
    const leftEdge = 8;
    const moveAmount = this.enemySpeed * dt * this.enemyDir;
    let willFlip = false;
    
    if (minX + moveAmount < leftEdge || maxX + moveAmount > rightEdge) {
      willFlip = true;
    }

    if (willFlip) {
      this.enemyDir *= -1;
      alive.forEach(e => e.y += this.enemyDrop);
      this.enemySpeed *= 1.02;
    } else {
      alive.forEach(e => e.x += moveAmount);
    }

    // Spawn enemy bullets
    this.spawnEnemyBullets(dt);

    // Check collisions
    this.checkCollisions();

    // Check if enemies reached player
    const bottomEnemy = alive.reduce((max, e) => Math.max(max, e.y + e.h), 0);
    if (bottomEnemy >= this.player.y - 12) {
      this.gameOver = true;
      this.running = false;
    }
  }

  setControls(controls) {
    this.controls = controls;
  }

  // Highscore funksjonalitet
  loadHighscore() {
    try {
      const saved = localStorage.getItem('spaceInvadersHighscore');
      return saved ? parseInt(saved, 10) : 0;
    } catch (e) {
      console.warn('Kunne ikke laste highscore:', e);
      return 0;
    }
  }

  saveHighscore() {
    try {
      localStorage.setItem('spaceInvadersHighscore', this.highscore.toString());
    } catch (e) {
      console.warn('Kunne ikke lagre highscore:', e);
    }
  }

  updateHighscore() {
    if (this.score > this.highscore) {
      this.highscore = this.score;
      this.saveHighscore();
      return true; // Ny highscore!
    }
    return false;
  }
}
